import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PunchClockRoundedIcon from '@mui/icons-material/PunchClockRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';

import { EmptyState, PageHeader, PageLayout, SectionCard } from '../../components/ui';
import { canAuthorPreApprovalRequest, getLocalRole, isHrTeamRole } from '../AllRequests/components/requestsUiUtils';

const CORE_REQUESTS = [
  {
    key: 'vacation',
    title: 'Vacation Request',
    subtitle: 'Annual, sick, emergency, unpaid, and special leave.',
    icon: BeachAccessIcon,
    colorKey: 'success',
    badge: 'Leave',
    route: '/vacation-request',
    required: ['Leave type', 'Start date', 'End date'],
  },
  {
    key: 'exit_entry',
    title: 'Exit / Entry Request',
    subtitle: 'Travel and re-entry approval for personal trips.',
    icon: FlightTakeoffIcon,
    colorKey: 'secondary',
    badge: 'Travel',
    route: '/exit-entry/new',
    required: ['Travel date', 'Return date', 'Country'],
  },
];

const BASE_HR_REQUEST_TYPES = [
  {
    value: 'to_leave',
    title: 'Permission to Leave',
    subtitle: 'Short leave during the working day.',
    icon: AccessTimeRoundedIcon,
    colorKey: 'info',
    badge: 'Most used',
    required: ['Date', 'From / To time', 'Reason'],
  },
  {
    value: 'loans',
    title: 'Loan Request',
    subtitle: 'Employee financial support and repayment workflow.',
    icon: PaymentsRoundedIcon,
    colorKey: 'success',
    required: ['Transaction date', 'Amount', 'No. of payments'],
  },
  {
    value: 'finance_claim',
    title: 'Finance Claim',
    subtitle: 'Expense reimbursement and claim processing.',
    icon: ReceiptLongRoundedIcon,
    colorKey: 'warning',
    required: ['Expense item', 'Transaction date', 'Amount'],
  },
  {
    value: 'misc',
    title: 'Miscellaneous Request',
    subtitle: 'General HR operational request.',
    icon: AssignmentRoundedIcon,
    colorKey: 'secondary',
    required: ['Requested item', 'Reason'],
  },
  {
    value: 'business_trip',
    title: 'Business Trip',
    subtitle: 'Mission and work-related travel approval.',
    icon: WorkOutlineRoundedIcon,
    colorKey: 'primary',
    required: ['Region', 'From date', 'To date'],
  },
  {
    value: 'bank',
    title: 'Bank Request',
    subtitle: 'Bank account setup or change request.',
    icon: AccountBalanceRoundedIcon,
    colorKey: 'info',
    required: ['Bank name', 'Account number', 'IBAN'],
  },
  {
    value: 'resignation',
    title: 'Resignation',
    subtitle: 'Formal resignation and clearance initiation.',
    icon: LogoutRoundedIcon,
    colorKey: 'error',
    required: ['Resignation date', 'Last working day', 'Reason'],
  },
  {
    value: 'personal_data_change',
    title: 'Personal Data Change',
    subtitle: 'Official employee data correction.',
    icon: ManageAccountsRoundedIcon,
    colorKey: 'success',
    required: ['Field to change', 'New value'],
  },
  {
    value: 'missing_punch',
    title: 'Missing Punch',
    subtitle: 'Correct missed attendance check-in or check-out records.',
    icon: PunchClockRoundedIcon,
    colorKey: 'warning',
    required: ['Punch date', 'Punch time', 'Reason'],
  },
];

const getColor = (theme, colorKey) => theme.palette[colorKey]?.main || theme.palette.primary.main;

const normalizeText = (value) => String(value || '').trim().toLowerCase();

function RequiredChips({ required = [], color }) {
  return (
    <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap">
      {required.map((item) => (
        <Chip
          key={item}
          size="small"
          label={item}
          sx={{
            height: 24,
            borderRadius: 999,
            color: 'text.primary',
            bgcolor: alpha(color, 0.08),
            border: '1px solid',
            borderColor: alpha(color, 0.14),
            fontWeight: 750,
          }}
        />
      ))}
    </Stack>
  );
}

function RequestCard({
  title,
  subtitle,
  icon: Icon,
  color,
  badge,
  required = [],
  selected = false,
  onOpen,
}) {
  const theme = useTheme();

  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      sx={{
        width: '100%',
        height: '100%',
        p: { xs: 1.35, sm: 1.55 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1.15,
        textAlign: 'left',
        border: '1px solid',
        borderColor: selected ? alpha(color, 0.48) : alpha(theme.palette.primary.main, 0.11),
        borderRadius: theme.hr?.radius?.lg || 2,
        bgcolor: selected ? alpha(color, theme.palette.mode === 'dark' ? 0.18 : 0.1) : alpha(theme.palette.background.paper, 0.84),
        color: 'text.primary',
        cursor: 'pointer',
        font: 'inherit',
        boxShadow: selected ? `0 18px 36px ${alpha(color, 0.14)}` : theme.hr?.shadow?.soft,
        transition: theme.transitions.create(['border-color', 'box-shadow', 'transform', 'background-color'], {
          duration: theme.transitions.duration.short,
        }),
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: alpha(color, 0.38),
          boxShadow: `0 18px 40px ${alpha(color, 0.12)}`,
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: theme.hr?.radius?.md || 2,
            display: 'grid',
            placeItems: 'center',
            color,
            bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.22 : 0.12),
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        {badge ? (
          <Chip
            size="small"
            label={badge}
            sx={{
              height: 24,
              color,
              bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.18 : 0.1),
              fontWeight: 800,
            }}
          />
        ) : null}
      </Stack>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.18 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
          {subtitle}
        </Typography>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 0.65, fontWeight: 850, letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          Required
        </Typography>
        <RequiredChips required={required} color={color} />
      </Box>

      <Stack direction="row" spacing={0.65} alignItems="center" sx={{ color, fontWeight: 850 }}>
        <Typography variant="caption" sx={{ fontWeight: 850 }}>
          Open form
        </Typography>
        <ArrowForwardIcon sx={{ fontSize: 16 }} />
      </Stack>
    </Box>
  );
}

export default function NewRequestPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const role = getLocalRole();
  // Pre-Approval is restricted to HR team + the management roles that
  // participate in its approval chain (HOD, Operations, CEO, Executive).
  // Regular employees, line managers, finance, and finance coordinators
  // don't see the card at all.
  const canSeePreApproval = canAuthorPreApprovalRequest(role);
  const isHrTeamMember = isHrTeamRole(role);
  const selectedType = searchParams.get('type') || searchParams.get('other_type');

  const hrRequestCatalog = useMemo(() => {
    const list = [...BASE_HR_REQUEST_TYPES];
    if (canSeePreApproval) {
      list.push({
        value: 'pre_approval',
        title: 'Pre-Approval Request',
        subtitle: 'Commercial and hiring pre-approval workflow.',
        icon: FactCheckRoundedIcon,
        colorKey: 'primary',
        badge: isHrTeamMember ? 'HR Team' : undefined,
        required: ['Candidate', 'Position', 'Project / Client', 'Department'],
      });
    }
    return list;
  }, [canSeePreApproval, isHrTeamMember]);

  const filteredHrRequests = useMemo(() => {
    const needle = normalizeText(query);
    if (!needle) return hrRequestCatalog;
    return hrRequestCatalog.filter((item) => {
      const haystack = normalizeText(`${item.title} ${item.subtitle} ${item.value} ${item.required.join(' ')}`);
      return haystack.includes(needle);
    });
  }, [hrRequestCatalog, query]);

  const totalAvailable = CORE_REQUESTS.length + hrRequestCatalog.length;

  const openHrRequest = (type) => {
    navigate(`/hr-requests/new?type=${encodeURIComponent(type)}`);
  };

  return (
    <PageLayout backgroundAccent compact>
      <PageHeader
        compact
        variant="dashboard"
        overline="Requests"
        title="Start a Request"
        subtitle="Choose the workflow that matches the request. Each card opens the correct form directly."
        actions={[
          <Button
            key="history"
            variant="outlined"
            startIcon={<HistoryRoundedIcon />}
            onClick={() => navigate('/employee/vacation-requests')}
          >
            My Requests
          </Button>,
        ]}
      />

      <Stack spacing={1.5}>
        <SectionCard tone="dashboard" contentSx={{ p: { xs: 1.4, md: 1.7 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                Request catalog
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, maxWidth: 760 }}>
                Use core forms for leave and travel. Use HR and administrative forms for finance, employee data, business trips, resignation, and other approvals.
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
              <Chip
                icon={<TaskAltRoundedIcon />}
                label={`${totalAvailable} forms`}
                sx={{ fontWeight: 850, bgcolor: alpha(theme.palette.primary.main, 0.09) }}
              />
              {canSeePreApproval ? (
                <Chip
                  label="Pre-approval enabled"
                  sx={{
                    fontWeight: 850,
                    color: theme.palette.secondary.dark,
                    bgcolor: alpha(theme.palette.secondary.main, 0.12),
                  }}
                />
              ) : null}
            </Stack>
          </Stack>
        </SectionCard>

        <SectionCard
          tone="dashboard"
          title="Core Requests"
          subheader="Most employee requests start here."
          contentSx={{ p: { xs: 1.3, sm: 1.5 } }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.15,
            }}
          >
            {CORE_REQUESTS.map((item) => (
              <RequestCard
                key={item.key}
                {...item}
                color={getColor(theme, item.colorKey)}
                onOpen={() => navigate(item.route)}
              />
            ))}
          </Box>
        </SectionCard>

        <SectionCard
          tone="dashboard"
          title="HR & Administrative Requests"
          subheader="Search by request name or required field, then open the matching form."
          action={
            <TextField
              size="small"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search requests"
              sx={{ minWidth: { xs: 220, sm: 280 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          }
          contentSx={{ p: { xs: 1.3, sm: 1.5 } }}
        >
          {filteredHrRequests.length ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))',
                  xl: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1.15,
              }}
            >
              {filteredHrRequests.map((item) => (
                <RequestCard
                  key={item.value}
                  title={item.title}
                  subtitle={item.subtitle}
                  icon={item.icon}
                  color={getColor(theme, item.colorKey)}
                  badge={item.badge}
                  required={item.required}
                  selected={selectedType === item.value}
                  onOpen={() => openHrRequest(item.value)}
                />
              ))}
            </Box>
          ) : (
            <EmptyState
              compact
              title="No matching request type"
              description="Try another keyword such as bank, loan, resignation, business trip, or reason."
            />
          )}
        </SectionCard>
      </Stack>
    </PageLayout>
  );
}
